/**
 * Shapes used by the publishing send-to-platform feature.
 *
 * Mirrors the API contract from api-scriptix#4100. Keep in sync when the
 * backend tightens the schema. Fields the frontend treats as optional are
 * optional here so renders survive a leaner backend payload.
 */

import type { PlatformId } from '@utils/types/connections/connection.ts';

export type PublishStatus =
  | 'queued'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'cancelled';

/**
 * Why the job failed, so the UI can show the right "next step". The
 * backend chooses one; `unknown` is the fallback for anything we have
 * not mapped yet.
 */
export type PublishFailureReason =
  | 'needs_reconnect'
  | 'quota_exceeded'
  | 'target_gone'
  | 'target_rejected'
  | 'network'
  | 'unknown';

export type PublishPayloadKind = 'captions' | 'transcript';

/**
 * What the user picks on step 3 of the publish dialog. The dialog never
 * builds this union by hand — it binds inputs to one branch and the
 * confirm handler passes the active branch.
 */
export type PublishPayload =
  | {
      kind: 'captions';
      /** ISO codes of caption tracks to send. */
      languages: string[];
    }
  | {
      kind: 'transcript';
      /** Export format id, matching the workspace export options. */
      format: string;
      /** Optional saved export template id. */
      template_id?: string;
    };

export interface YouTubeTarget {
  kind: 'youtube_video';
  /** YouTube video id, e.g. `dQw4w9WgXcQ`. */
  id: string;
  title: string;
  /** Thumbnail URL (any resolution — picker renders at a fixed size). */
  thumbnail_url?: string;
  /** ISO 8601 string, used only for sort/display. */
  published_at?: string;
  /** Video length in seconds for a human-readable duration. */
  duration_seconds?: number;
}

/**
 * Vimeo video the user picks to attach captions to.
 *
 * The connector only supports captions on existing videos — Vimeo's
 * resumable upload flow and transcript attachments are deliberately
 * out of scope for #4163, so there is no ``vimeo_new_video`` target
 * sibling.
 */
export interface VimeoTarget {
  kind: 'vimeo_video';
  /** Numeric Vimeo video id, e.g. ``123456789``. */
  id: string;
  title: string;
  thumbnail_url?: string;
  published_at?: string;
  duration_seconds?: number;
}

/**
 * "Upload as new video" branch — Scriptix streams the session's source
 * media to YouTube's resumable upload endpoint, captures the minted
 * video id, then runs the normal captions.insert path against it.
 *
 * ``privacy`` is the user's requested status; the backend clamps it
 * (``public`` falls back to ``private`` until Google's audit has
 * cleared, see api-scriptix#4101 follow-up Section 2), so the UI
 * never has to lie about what will land. The default is ``private``
 * — safest for a brand-new upload the user hasn't reviewed yet.
 *
 * `id` is empty on the way in — YouTube assigns it on upload — but the
 * target discriminator still lives on `kind`, so dialog code treats
 * this like any other target without special-casing.
 */
export type YouTubeNewVideoPrivacy = 'private' | 'unlisted' | 'public';

export interface YouTubeNewVideoTarget {
  kind: 'youtube_new_video';
  /** Empty on the way in; populated with the new video id after success. */
  id: '';
  /** Video title that will land on YouTube — required. */
  title: string;
  description?: string;
  /** Requested privacy status; defaults to ``private`` for new uploads. */
  privacy?: YouTubeNewVideoPrivacy;
}

export interface IBabsAgendaItem {
  /** Agenda item id inside the meeting. */
  id: string;
  title: string;
}

export interface IBabsTarget {
  kind: 'ibabs_meeting';
  /** Meeting id in the connected iBabs site. */
  id: string;
  title: string;
  /** ISO 8601 date of the meeting. */
  starts_at?: string;
  agenda_items: IBabsAgendaItem[];
}

export type PublishTarget =
  | YouTubeTarget
  | YouTubeNewVideoTarget
  | VimeoTarget
  | IBabsTarget;

/**
 * What the user actually confirmed: the chosen target plus — for iBabs
 * — the chosen agenda item. YouTube has no sub-target.
 */
export interface PublishTargetSelection {
  platform: PlatformId;
  connection_id: string;
  target: PublishTarget;
  /** Agenda item id when platform is iBabs. */
  agenda_item_id?: string;
}

export interface PublishTargetsResponse {
  /** Server-side cursor / token when the list is paged. */
  next_cursor?: string;
  result: PublishTarget[];
}

/**
 * Payload shape mirrors api-scriptix#4100's
 * ``StartPublishJobRequest``: ``target_ref`` is an opaque dict the
 * backend stores and the connector reads, not a nested object. The
 * agenda item id lives inside that dict for iBabs targets so no
 * sibling field is needed.
 */
export interface StartPublishJobPayload {
  connection_id: string;
  target_ref: Record<string, unknown>;
  payload: PublishPayload;
}

export interface PublishJob {
  id: string;
  session_id: string;
  document_id: string;
  connection_id: string;
  platform: PlatformId;
  status: PublishStatus;
  /** 0 → 100 while running; absent for terminal states. */
  progress_percent?: number;
  /**
   * Reference to the thing the user picked (video id / meeting id). The
   * frontend keeps the original selection around; this is only used
   * when a job is resumed from a cold reload.
   */
  target_ref?: {
    kind: PublishTarget['kind'];
    id: string;
    title?: string;
  };
  /** Set once the job finishes successfully. */
  result_url?: string;
  failure?: {
    reason: PublishFailureReason;
    /** User-visible message from the backend. May be localized. */
    message?: string;
  };
  /** ISO 8601 timestamps. */
  started_at?: string;
  finished_at?: string;
}

export interface PublishJobResponse {
  result: PublishJob;
}

export interface PublishHistoryEntry {
  id: string;
  platform: PlatformId;
  status: PublishStatus;
  /** The target this attempt was for, so the UI can group re-publishes. */
  target_ref?: {
    kind: PublishTarget['kind'];
    id: string;
    title?: string;
  };
  result_url?: string;
  failure?: {
    reason: PublishFailureReason;
    message?: string;
  };
  /** Who clicked Publish. */
  published_by?: { id?: string; name: string };
  /** ISO 8601 timestamps. */
  published_at?: string;
}

export interface PublishHistoryResponse {
  count: number;
  total_results: number;
  result: PublishHistoryEntry[];
}

/**
 * True when `prev` and `candidate` point at the same target+payload so
 * the dialog can show the "this replaces the earlier version" warning.
 * YouTube re-publishes replace the caption tracks on the video;
 * iBabs re-publishes replace the attached document for the agenda
 * item. Both are keyed by (platform, target_ref.id, agenda_item_id?).
 */
export function isSameTarget(
  prev: PublishHistoryEntry,
  candidate: PublishTargetSelection
): boolean {
  if (!prev.target_ref) return false;
  if (prev.platform !== candidate.platform) return false;
  // A fresh upload has no id yet, so there is nothing for a prior
  // publish to collide with — treat every new-video request as unique.
  if (candidate.target.kind === 'youtube_new_video') return false;
  if (prev.target_ref.id !== candidate.target.id) return false;
  if (candidate.target.kind === 'ibabs_meeting') {
    const prevAgenda =
      prev.target_ref.kind === 'ibabs_meeting'
        ? (prev as PublishHistoryEntry & { agenda_item_id?: string })
            .agenda_item_id
        : undefined;
    return prevAgenda === candidate.agenda_item_id;
  }
  return true;
}

export function isTerminalPublishStatus(status: PublishStatus): boolean {
  return (
    status === 'succeeded' || status === 'failed' || status === 'cancelled'
  );
}
